import { Button, Flex, Space, Text } from '@mantine/core';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { useWindowSize } from '../../../../hooks';
import { Loading } from '../../../../component';
import { useUserMeStore } from '../../../../store';
import { useNachversandStore } from '../../../../store/NachversandStore';
import { Routes } from '../../../../route/Routes';
import { BaseLayout } from '../../navigation';
import { bodyTextProps } from './utils';
import { CurrentNachversand, NachversandHistory } from './components';

const sectionTitleProps = { fw: 600, size: '21px', lh: '24px' } as const;

/**
 * Part 2 Nachversand: book the video call for the next purchase by post, and the purchases so
 * far. Group B only (route), and only while nachversandEligible is true — which is known only
 * after /users/me, so this screen checks it itself.
 */
export const VersandScreen = observer(() => {
  const isSmallScreen = useWindowSize(500);
  const { t } = useTranslation('versand');
  const userMeStore = useUserMeStore();
  const store = useNachversandStore();

  useEffect(() => store.loadAndReloadOnVisible(), [store]);

  const eligibleForMe =
    userMeStore.myInfo?.Participant?.nachversandEligible === true;
  if (!eligibleForMe && userMeStore.loadingInfo) {
    return <Loading />;
  }
  // /users/me is loaded once per session, so the server's answer has the last word after a move
  if (!eligibleForMe || store.nachversand?.eligible === false) {
    return <Navigate to={Routes.Study} />;
  }
  if (store.nachversand === null && !store.loadFailed) {
    return <Loading />;
  }

  return (
    <BaseLayout>
      <Flex
        direction="column"
        maw={1100}
        gap={20}
        bg="#fff"
        align="flex-start"
        p={isSmallScreen ? 20 : 40}
      >
        <Text fw="600" size="28px" lh="38px">
          {t('Title')}
        </Text>
        {store.nachversand === null ? (
          <>
            <Text {...bodyTextProps}>{t('LoadFailed')}</Text>
            <Button size="sm" onClick={store.load}>
              {t('Retry')}
            </Button>
          </>
        ) : (
          <>
            <Text {...bodyTextProps}>{t('Intro')}</Text>

            <Space />

            <Text {...sectionTitleProps}>{t('Current.Title')}</Text>
            <CurrentNachversand current={store.nachversand.current} />

            <Space />

            <Text {...sectionTitleProps}>{t('History.Title')}</Text>
            <NachversandHistory history={store.nachversand.history} />
          </>
        )}
      </Flex>
    </BaseLayout>
  );
});
